// Queries API Layer - Low-level HTTP calls
import { getToken } from "../../../../../shared/data/storage/authStorage";
import {
  QueryDTO,
  CreateQueryRequestDTO,
  CreateSubQueryRequestDTO,
  QueryResponseDTO,
  QueriesResponseDTO,
  PaginatedQueriesResponseDTO,
} from "../models/QueryDTO";
import {
  CreateCommentRequestDTO,
  CommentResponseDTO,
  CommentsResponseDTO,
} from "../models/QueryCommentDTO";
import { HistoryResponseDTO } from "../models/QueryHistoryDTO";
import { QueryCountsResponseDTO } from "../models/QueryCountsDTO";
import { API_BASE_URL } from "../../../../../config/api";


class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const createHeaders = (includeAuth = true): Record<string, string> => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  if (includeAuth) {
    const token = getToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }

  return headers;
};

const makeRequest = async (endpoint: string, options: any = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const config = {
    headers: createHeaders(options.includeAuth !== false),
    ...options,
  };

  try {
    const response = await fetch(url, config);
    const data = await response.json();

    if (!response.ok) {
      throw new APIError(
        data.message || `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        data
      );
    }

    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }

    console.error(`API request failed for ${endpoint}:`, error);
    const err = error as any;
    throw new APIError(
      err.message || "Network error occurred",
      err.status || 0,
      { originalError: error }
    );
  }
};

export class QueriesApi {
  // Create Operations
  async create(payload: CreateQueryRequestDTO): Promise<QueryResponseDTO> {
    return makeRequest("/api/queries", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async createSubQuery(
    parentQueryId: number,
    payload: Omit<CreateSubQueryRequestDTO, "parent_query_id">
  ): Promise<QueryResponseDTO> {
    return makeRequest(`/api/queries/${parentQueryId}/sub-queries`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  // Read Operations
  async getAll(params?: {
    source?: string;
    status?: string;
    priority?: string;
    assigned_to?: number;
    raised_by?: number;
    sort_by?: string;
    sort_order?: string;
  }): Promise<QueriesResponseDTO> {
    const queryParams = new URLSearchParams();
    if (params?.source) queryParams.append("source", params.source);
    if (params?.status) queryParams.append("status", params.status);
    if (params?.priority) queryParams.append("priority", params.priority);
    if (params?.assigned_to)
      queryParams.append("assigned_to", String(params.assigned_to));
    if (params?.raised_by)
      queryParams.append("raised_by", String(params.raised_by));
    if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params?.sort_order) queryParams.append("sort_order", params.sort_order);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/api/queries?${queryString}`
      : "/api/queries";

    return makeRequest(endpoint, { method: "GET" });
  }

  async getDetails(queryId: number, clientView?: boolean): Promise<QueryResponseDTO> {
    const url = clientView 
      ? `/api/queries/${queryId}?client_view=true`
      : `/api/queries/${queryId}`;
    return makeRequest(url, { method: "GET" });
  }

  async getQueriesBySource(
    source: string,
    sourceId?: number,
    params?: {
      status?: string;
      priority?: string;
      sort_by?: string;
      sort_order?: string;
      limit?: number;
      offset?: number;
      search?: string;
    }
  ): Promise<PaginatedQueriesResponseDTO> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append("status", params.status);
    if (params?.priority) queryParams.append("priority", params.priority);
    if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params?.sort_order) queryParams.append("sort_order", params.sort_order);
    if (params?.limit !== undefined)
      queryParams.append("limit", String(params.limit));
    if (params?.offset !== undefined)
      queryParams.append("offset", String(params.offset));
    if (params?.search) queryParams.append("search", params.search);

    const queryString = queryParams.toString();
    const baseUrl = sourceId
      ? `/api/queries/source/${source}/${sourceId}`
      : `/api/queries/source/${source}`;
    const endpoint = queryString ? `${baseUrl}?${queryString}` : baseUrl;

    return makeRequest(endpoint, { method: "GET" });
  }

  async getAssignedToMe(
    userId: number,
    params?: {
      source?: string;
      status?: string;
      priority?: string;
      sort_by?: string;
      sort_order?: string;
      limit?: number;
      offset?: number;
      search?: string;
      assigned_to?: number;
      raised_by?: number;
    }
  ): Promise<PaginatedQueriesResponseDTO> {
    const queryParams = new URLSearchParams();
    if (params?.source) queryParams.append("source", params.source);
    if (params?.status) queryParams.append("status", params.status);
    if (params?.priority) queryParams.append("priority", params.priority);
    if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params?.sort_order) queryParams.append("sort_order", params.sort_order);
    if (params?.limit !== undefined)
      queryParams.append("limit", String(params.limit));
    if (params?.offset !== undefined)
      queryParams.append("offset", String(params.offset));
    if (params?.search) queryParams.append("search", params.search);
    if (params?.assigned_to !== undefined)
      queryParams.append("assigned_to", String(params.assigned_to));
    if (params?.raised_by !== undefined)
      queryParams.append("raised_by", String(params.raised_by));

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/api/queries/${userId}/assigned?${queryString}`
      : `/api/queries/${userId}/assigned`;

    return makeRequest(endpoint, { method: "GET" });
  }

  async getRaisedByMe(
    userId: number,
    params?: {
      source?: string;
      status?: string;
      priority?: string;
      sort_by?: string;
      sort_order?: string;
      limit?: number;
      offset?: number;
      search?: string;
      assigned_to?: number;
      raised_by?: number;
    }
  ): Promise<PaginatedQueriesResponseDTO> {
    const queryParams = new URLSearchParams();
    if (params?.source) queryParams.append("source", params.source);
    if (params?.status) queryParams.append("status", params.status);
    if (params?.priority) queryParams.append("priority", params.priority);
    if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params?.sort_order) queryParams.append("sort_order", params.sort_order);
    if (params?.limit !== undefined)
      queryParams.append("limit", String(params.limit));
    if (params?.offset !== undefined)
      queryParams.append("offset", String(params.offset));
    if (params?.search) queryParams.append("search", params.search);
    if (params?.assigned_to !== undefined)
      queryParams.append("assigned_to", String(params.assigned_to));
    if (params?.raised_by !== undefined)
      queryParams.append("raised_by", String(params.raised_by));

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/api/queries/${userId}/raised?${queryString}`
      : `/api/queries/${userId}/raised`;

    return makeRequest(endpoint, { method: "GET" });
  }

  async getSubQueries(queryId: number): Promise<QueriesResponseDTO> {
    return makeRequest(`/api/queries/${queryId}/sub-queries`, {
      method: "GET",
    });
  }

  async getMyCounts(): Promise<QueryCountsResponseDTO> {
    return makeRequest("/api/queries/my-counts", { method: "GET" });
  }

  // Update Operations
  async update(
    queryId: number,
    updates: Partial<QueryDTO>
  ): Promise<QueryResponseDTO> {
    return makeRequest(`/api/queries/${queryId}/update`, {
      method: "POST",
      body: JSON.stringify(updates),
    });
  }

  // Delete Operations
  async delete(
    queryId: number
  ): Promise<{ success: boolean; message: string }> {
    return makeRequest(`/api/queries/${queryId}/delete`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  }

  // Resolve Operations
  async resolve(queryId: number): Promise<QueryResponseDTO> {
    return makeRequest(`/api/queries/${queryId}/resolve`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  }

  // Comment Operations
  async addComment(
    queryId: number,
    payload: CreateCommentRequestDTO
  ): Promise<CommentResponseDTO> {
    return makeRequest(`/api/queries/${queryId}/comments`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async getComments(queryId: number): Promise<CommentsResponseDTO> {
    return makeRequest(`/api/queries/${queryId}/comments`, { method: "GET" });
  }

  // History Operations
  async getHistory(queryId: number): Promise<HistoryResponseDTO> {
    return makeRequest(`/api/queries/${queryId}/history`, { method: "GET" });
  }
}

// Export singleton instance
export const queriesApi = new QueriesApi();
